import { formatCalendarDate } from '@tms/core';

import type { LedgerEntry } from '../../api';
import { todayInIndia } from './today';

/**
 * `LED-05`'s list, flattened into one array of day bars and rows.
 *
 * Flat rather than nested, because the list is virtualised: a header rendered outside the window is a
 * header that disappears when its rows scroll past, and a nested structure would need the virtualiser to
 * understand two levels. One array of two kinds is the shape a windowing library can measure.
 */
export type LedgerRow =
  | {
      readonly type: 'day';
      readonly occurredOn: string;
      /**
       * The balance at the **end** of that day (`FEAT-011 §5.6`).
       *
       * The page is newest-first, so the first row of a day group is the latest entry of that day, and its
       * running balance is exactly the day's closing balance. That is why the server puts the balance on
       * each row rather than sending one figure per page: the client groups and reads, and computes
       * nothing.
       */
      readonly balanceMinor: number | undefined;
    }
  | { readonly type: 'entry'; readonly entry: LedgerEntry };

/**
 * Groups a newest-first page into day bars and rows.
 *
 * It preserves the order it was given and never sorts. The server returns `OccurredOn DESC, Id DESC` off
 * the index (`FEAT-011 §5.4`), and a client that re-sorted would be re-deriving an order it was handed —
 * and would get the same-day tie-break wrong, because it cannot see the internal id the server ordered by.
 */
export function groupByDay(entries: readonly LedgerEntry[]): readonly LedgerRow[] {
  const rows: LedgerRow[] = [];
  let currentDay: string | null = null;

  for (const entry of entries) {
    if (entry.occurredOn !== currentDay) {
      currentDay = entry.occurredOn;
      rows.push({
        type: 'day',
        occurredOn: entry.occurredOn,
        balanceMinor: entry.runningBalanceMinor ?? undefined,
      });
    }

    rows.push({ type: 'entry', entry });
  }

  return rows;
}

/**
 * "Today · Sun 16 Aug", "Yesterday · Sat 15 Aug", or just the date.
 *
 * "Today" is a client-side idea — it depends on where the person is standing — which is why the server
 * sends a date and this turns it into a word (`FEAT-011 §3.3`).
 */
export function dayLabel(occurredOn: string, today: string = todayInIndia()): string {
  const formatted = formatCalendarDate(occurredOn);

  if (occurredOn === today) {
    return `Today · ${formatted}`;
  }

  const yesterday = new Date(`${today}T00:00:00Z`);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);

  return occurredOn === yesterday.toISOString().slice(0, 10) ? `Yesterday · ${formatted}` : formatted;
}
